import Input from "@/components/Input/Input";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BYTE_UNITS,
  type Unit,
  convertSize,
  formatFileSize,
  formatFloatNumber,
} from "@/utils/number/si-prefix";
import { useMemo, useState } from "react";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  /** バイト単位での値 */
  value?: number;
  onChange?: (value?: number) => void;
  inputClassName?: string;
  /** デフォルトの表示/入力単位（デフォルト: "B"） */
  defaultUnit?: Unit;
};

/**
 * Card-based Form / ByteSizeItem
 * バイト値を扱うためのフォームアイテム
 */
export const CFormByteSizeItem = buildCFormItem((props: Props) => {
  const { defaultUnit = "B", ...restProps } = props;

  // 単位のローカルステートを管理
  const [selectedUnit, setSelectedUnit] = useState<Unit>(defaultUnit);

  // 読み取り専用時の表示
  const readDisplayValue = (() => {
    if (props.value === undefined) return "-";
    return formatFileSize(props.value);
  })();

  // 編集時の値を計算
  const editDisplayValue = useMemo(() => {
    if (props.value === undefined) return "";
    try {
      const convertedValue = convertSize(props.value, "B", selectedUnit);
      return formatFloatNumber(convertedValue, 6).toString();
    } catch {
      return "";
    }
  }, [props.value, selectedUnit]);

  const handleValueChange = (inputValue: string) => {
    if (inputValue === "") {
      props.onChange?.(undefined);
      return;
    }

    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) return;

    try {
      const bytesValue = convertSize(numValue, selectedUnit, "B");
      props.onChange?.(Math.round(bytesValue));
    } catch {
      // 無効な変換の場合は何もしない
    }
  };

  const handleUnitChange = (newUnit: Unit) => {
    setSelectedUnit(newUnit);

    // 現在の入力値がある場合は、新しい単位での値に変換
    if (editDisplayValue && editDisplayValue !== "") {
      const numValue = parseFloat(editDisplayValue);
      if (!isNaN(numValue)) {
        try {
          const bytesValue = convertSize(numValue, newUnit, "B");
          props.onChange?.(Math.round(bytesValue));
        } catch {
          // 無効な変換の場合は何もしない
        }
      }
    }
  };

  return (
    <CFormItemBase
      {...restProps}
      readComponent={<Text variant="p">{readDisplayValue}</Text>}
      writeComponent={
        <HStack className="gap-2 items-center">
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={editDisplayValue}
            onChange={(e) => {
              handleValueChange(e.target.value);
            }}
            className={props.inputClassName ?? "w-[120px]"}
            disabled={props.readonly}
          />
          <Select
            value={selectedUnit}
            onValueChange={handleUnitChange}
            disabled={props.readonly}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BYTE_UNITS.map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      }
    />
  );
});
